import { useToast } from '@scality/core-ui';
import { type UseMutationResult, useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import { RETRYING_TEMPLATE, THINKING_TEMPLATE } from '../../components/chat-messages/thinkingTemplates';
import { type ConversationScope, GuardianContext } from '../../components/GuardianProvider';
import { dashboardMetadataKey } from '../../dashboard/hooks/useDashboardMetadata';
import type {
  ChatRequestSourceEnum,
  ChatResponse,
  ConversationMetadata,
  DashboardAgentState,
  EventResponse,
  FetchError,
  Message,
  MessageRoleEnum,
  ResponseError,
} from '../../gen/openapi';
import { type JsonRpcToolCallRequest, useMcpBridge } from '../../mcp/McpBridgeProvider';
import { toolCallResources } from '../../mcp/toolCallDisplay';
import {
  type ChatFeedEntry,
  type ClientChatResponse,
  isToolTrail,
  type McpCallRecord,
  type ToolOutcomeRecord,
} from '../../types/ClientMessage';
import { logError } from '../../utils/logger';
import { useApiClient } from '../useApiClient';
import { BackgroundTasksContext } from '../useBackgroundTasks';
import { useChatTitle } from '../useChatTitle';
import { useChatUrlInstanceIds } from '../useChatUrlInstanceIds';
import { useSource } from '../useSource';
import { getChatErrorMessage } from './chatErrorMessage';
import { newMessage, toApiMessage } from './chatMessages';
import {
  ChatActionType,
  type ChatState,
  chatReducer,
  initialChatState,
  selectActiveAuthorizationRequest,
  selectActiveCall,
  selectInFlightCount,
  selectIsBusy,
  ToolCallStatus,
} from './chatReducer';
import { messagesKey } from './conversationKeys';
import { buildChatFeed, isTrailLive } from './toolTrail';
import { readBackgroundTaskStart, useBackgroundTaskHandoff } from './useBackgroundTaskHandoff';
import { useScrollToLastMessage } from './useScrollToLastMessage';

/**
 * Reducer-driven chat orchestration (in the style of useMultipartDownloadUpload).
 *
 * ONE owner runs the whole conversation + tool-call loop; `useChat()` is a thin
 * context consumer, so the ~10 call sites all read the same machine. Clean split:
 *   - react-query  -> persisted message history + the chat POST mutation
 *   - chatReducer  -> the tool-loop state machine (single source of truth)
 *   - the driver   -> the ONLY place side-effects happen, derived from state
 *   - McpBridge    -> pure postMessage transport
 *
 * Every async event (send settled, host ACK/RESPONSE, timeout, approve/reject)
 * just `dispatch`es an action. No override props, no per-request tool list, no
 * refs-for-cycles, no queue in the bridge.
 */

const ACK_TIMEOUT_MS = 30_000;
const RESPONSE_TIMEOUT_MS = 60_000;

export type ChatMutationVariables = {
  query: string;
  messages?: ClientChatResponse[];
  role?: MessageRoleEnum;
  /** Send the user turn to the agent but keep it out of the visible feed (e.g. auto-summary). */
  hidden?: boolean;
  conversationId?: string;
  scope?: ConversationScope;
  /** On a tool-result turn: how each call ended, recorded for the activity trail. */
  toolOutcomes?: Record<string, ToolOutcomeRecord>;
};

type ChatMutation = UseMutationResult<
  ChatResponse,
  // What the generated client throws in practice. TS can't enforce throw
  // types, so getChatErrorMessage still discriminates at runtime.
  ResponseError | FetchError,
  ChatMutationVariables,
  { previousMessages: ClientChatResponse[] }
>;

/** What a caller supplies to leave feedback — the conversation itself is added
 * by the provider, so callers never pass the message history. */
export type ConversationFeedback = {
  isPositiveFeedback: boolean;
  categories: string[];
  feedbackMessage: string;
};

type FeedbackMutation = UseMutationResult<EventResponse, Error, ConversationFeedback, unknown>;

/**
 * The chat's capabilities, grouped by what you can do with a conversation.
 * This is the entire public surface of `useChat()`.
 */
export type ChatApi = {
  /** The conversation as it should be rendered. */
  conversation: {
    /** Messages to display (assistant/user turns + the thinking indicator). */
    messages: ChatFeedEntry[];
    /** No messages yet — a fresh chat. */
    isNew: boolean;
    /** The assistant has produced at least one message. */
    hasAssistantReplied: boolean;
  };

  /** Backend metadata for the current conversation. */
  conversationMetadata: ConversationMetadata | null;

  /** Send a message (user turn). Failures surface as an error message in the
   * conversation (see onError), so callers never handle rejections.
   * `*Async` resolves with the assistant reply. */
  sendMessage: ChatMutation['mutate'];
  sendMessageAsync: ChatMutation['mutateAsync'];
  /** Re-send the last user prompt, dropping the trailing error message. */
  retryLastMessage: () => void;
  /** A request to Guardian is in flight (including retries). */
  isResponding: boolean;

  /** A tool Guardian wants to run that needs the user's go-ahead. Tools are
   * approved one at a time, in order — this is the single tool being asked
   * about right now. */
  toolAuthorization: {
    /** The tool awaiting the user's decision, or null. */
    pending: JsonRpcToolCallRequest | null;
    /** A tool call has been dispatched to the host and not yet resolved.
     * (Calls run one at a time — the loop is serial.) */
    isRunning: boolean;
    /** Approve this tool; it runs, then the next tool is handled. */
    approve: () => void;
    /** Reject this tool — and, with it, every tool queued behind it. */
    reject: () => void;
  };

  /** Rate the conversation; the message history is attached automatically. */
  feedback: {
    submit: FeedbackMutation['mutate'];
    isSubmitting: boolean;
  };

  /** Presentational state used by <Chat> to drive the loader/animation. */
  rendering: {
    /** A thinking placeholder is appended to `conversation.messages`. */
    isThinking: boolean;
    /** Count of pre-existing messages on load, so history isn't animated. */
    historisedMessageCount: number;
  };
};

/**
 * Exported so a caller can supply a `ChatApi` of its own instead of the live provider.
 * The only such caller is `test-utils/FakeChatProvider`, which drives Storybook stories
 * off a static conversation — the app itself always goes through `ChatProvider` below.
 */
export const ChatContext = createContext<ChatApi | null>(null);

export const useChat = (): ChatApi => {
  const value = useContext(ChatContext);
  if (!value) throw new Error('useChat must be used within a ChatProvider');
  return value;
};

const THINKING_MESSAGE = newMessage(THINKING_TEMPLATE, 'assistant');
const RETRYING_MESSAGE = newMessage(RETRYING_TEMPLATE, 'assistant');

export const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const { conversationId, conversationScope, lastChatCardRef, selectedAgent } = useContext(GuardianContext);
  const transport = useMcpBridge();
  const queryClient = useQueryClient();
  const { chat: chatApi, events } = useApiClient();
  const { selectedInstanceIds } = useChatUrlInstanceIds();
  const source = useSource();
  const toast = useToast();

  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const stateRef = useRef<ChatState>(state);
  stateRef.current = state;

  // A dashboard analysis lives under its own cache root, so it never reaches the conversation
  // history; being ephemeral (fresh per visit), it is not persisted either.
  const messagesKeyOf = useCallback(
    (id: string = conversationId) => messagesKey(conversationScope, id),
    [conversationScope, conversationId],
  );
  const persistConversation = conversationScope === 'chat';
  const { data: chatQueryData = [] } = useQuery<ClientChatResponse[], Error>({
    queryKey: messagesKeyOf(),
    initialData: [],
    staleTime: Infinity,
    gcTime: Infinity,
    meta: { shouldPersist: persistConversation },
  });
  // Observed so the metadata cache entry stays persisted. The mutation still reads metadata by
  // conversation id from the cache (mutationFn/onSuccess), so an in-flight send targets the right
  // one; this binding only exposes the *current* conversation's value to context consumers.
  const { data: conversationMetadata = null } = useQuery<ConversationMetadata | null, Error>({
    queryKey: ['chatMetadata', conversationId],
    initialData: null,
    staleTime: Infinity,
    gcTime: Infinity,
    meta: { shouldPersist: persistConversation },
  });

  useChatTitle(conversationId, chatQueryData, conversationScope);

  const chatMutation = useMutation<
    ChatResponse,
    ResponseError | FetchError,
    ChatMutationVariables,
    { previousMessages: ClientChatResponse[] }
  >({
    mutationKey: messagesKeyOf(),
    mutationFn: ({ query, messages, role, conversationId: cid = conversationId, scope = conversationScope }) => {
      const cachedMessages = queryClient.getQueryData<ClientChatResponse[]>(messagesKey(scope, cid)) ?? [];
      const clientMessages = messages ?? (cachedMessages.length > 0 ? cachedMessages : [newMessage(query, role)]);

      const metadata = queryClient.getQueryData<ConversationMetadata | null>(['chatMetadata', cid]) ?? null;
      const dashboardAgentState = queryClient.getQueryData<DashboardAgentState | null>(dashboardMetadataKey(cid));
      const outgoingMetadata = dashboardAgentState ? { ...(metadata ?? {}), dashboardAgentState } : metadata;
      return chatApi.chatApiV1ChatPost({
        chatRequest: {
          messages: clientMessages.map(toApiMessage),
          conversationId: cid,
          uiComponentVersion: 1,
          instanceIds: selectedInstanceIds,
          conversationMetadata: outgoingMetadata,
          agentSelected: selectedAgent?.id ?? undefined,
          // Live tool list straight from the transport — never stale, since one
          // owner reads current context.
          mcpTools: transport.mcpTools ?? [],
          source: source as ChatRequestSourceEnum | undefined,
          platformVersion: transport.platformVersion,
        },
      });
    },
    onMutate({
      query,
      messages,
      role,
      hidden,
      toolOutcomes,
      conversationId: cid = conversationId,
      scope = conversationScope,
    }) {
      dispatch({ type: ChatActionType.SendStarted });
      const key = messagesKey(scope, cid);
      const outgoing = newMessage(query, role, undefined, hidden);
      // Read the LIVE cache (not a stale closure) so a message a background writer — e.g. the
      // update poller — just appended is not dropped.
      const chatWithUserMessage = messages ?? [
        ...(queryClient.getQueryData<ClientChatResponse[]>(key) ?? []),
        toolOutcomes ? { ...outgoing, mcpKind: 'mcp_response' as const, mcpOutcomes: toolOutcomes } : outgoing,
      ];
      queryClient.setQueryData<ClientChatResponse[]>(key, chatWithUserMessage);
      return { previousMessages: chatWithUserMessage };
    },
    onSuccess: (response, vars, context) => {
      const cid = vars.conversationId ?? conversationId;
      const key = messagesKey(vars.scope ?? conversationScope, cid);
      // Assistant asked us to run front-end tools: the backend sends the turn's
      // calls as a JSON array (a bare object is tolerated for back-compat). Record
      // them as one history entry of N fences and hand each to the reducer; the
      // driver runs them in order.
      if (response.conversationMetadata?.usedAgents?.[0] === 'mcp_call') {
        try {
          const parsed = JSON.parse(response.content);
          const rawCalls: Record<string, unknown>[] = Array.isArray(parsed) ? parsed : [parsed];

          const calls = rawCalls.map((entry) => {
            const { explanation, ...jsonrpcRequest } = entry;
            const request = jsonrpcRequest as JsonRpcToolCallRequest;
            const tool = transport.mcpTools.find((t) => t.name === request.params?.name);
            const record: McpCallRecord = {
              id: request.id,
              name: request.params.name,
              explanation: typeof explanation === 'string' ? explanation : undefined,
              requiresApproval: tool?.annotations?.readOnlyHint !== true,
              resources: toolCallResources(request.params.arguments, tool?.inputSchema),
            };
            return { request, record };
          });

          const mcpCallEntry: ClientChatResponse = {
            ...response,
            content: calls.map(({ request }) => `\`\`\`json+McpCall\n${JSON.stringify(request)}\n\`\`\``).join('\n\n'),
            mcpKind: 'mcp_call',
            mcpCalls: calls.map((c) => c.record),
          };
          queryClient.setQueryData<ClientChatResponse[]>(key, (prev) => [
            ...(prev ?? context?.previousMessages ?? []),
            mcpCallEntry,
          ]);

          calls.forEach(({ request, record }, order) => {
            dispatch({
              type: ChatActionType.ToolRequested,
              request,
              requiresAuthorization: record.requiresApproval,
              order,
            });
          });
        } catch {
          queryClient.setQueryData<ClientChatResponse[]>(key, (prev) => [
            ...(prev ?? context?.previousMessages ?? []),
            newMessage('Guardian failed to parse a tool call.', 'assistant', true),
          ]);
        }
        return;
      }

      queryClient.setQueryData<ClientChatResponse[]>(key, (prev) => [
        ...(prev ?? context?.previousMessages ?? []),
        response,
      ]);
      queryClient.setQueryData<ConversationMetadata | null>(
        ['chatMetadata', cid],
        response.conversationMetadata ? { ...response.conversationMetadata, dashboardAgentState: undefined } : null,
      );
    },
    onError: async (error, vars, context) => {
      logError(error, 'chatMutation failed');
      const cid = vars.conversationId ?? conversationId;
      const key = messagesKey(vars.scope ?? conversationScope, cid);
      const message = await getChatErrorMessage(error);
      queryClient.setQueryData<ClientChatResponse[]>(key, (prev) => [
        ...(prev ?? context?.previousMessages ?? []),
        newMessage(message, 'assistant', true),
      ]);
    },
    onSettled: () => dispatch({ type: ChatActionType.SendSettled }),
    retry: 1,
  });

  // Long-running WebMCP tools: the tracking, the captured request context and the
  // completion hand-off turn all live in useBackgroundTaskHandoff; the provider only
  // feeds it the task ids it sees go by on the bridge.
  const { tasks: backgroundTasks, registerBackgroundTask } = useBackgroundTaskHandoff();

  // Host ACK/RESPONSE -> reducer actions. Registered once; the cleanup
  // unsubscribes when the provider unmounts (or `subscribe` identity changes).
  useEffect(() => {
    const unsubscribe = transport.subscribe((event) => {
      if (event.kind === 'ack') dispatch({ type: ChatActionType.ToolAcknowledged, id: event.id });
      else if (event.kind === 'response') {
        // A result carrying { taskId, status: 'working' } means a background op just
        // started — hand the id to the tracker, which owns it from here.
        const started = readBackgroundTaskStart(event.result);
        if (started) registerBackgroundTask(started.taskId, started.statusMessage ?? 'Background task');
        dispatch({ type: ChatActionType.ToolResponded, id: event.id, result: event.result, error: event.error });
      }
    });
    return unsubscribe;
  }, [transport.subscribe, registerBackgroundTask]);

  // Reset the loop when the conversation changes (messages are keyed in cache).
  // biome-ignore lint/correctness/useExhaustiveDependencies: conversationId is the intentional reset trigger
  useEffect(() => {
    dispatch({ type: ChatActionType.Reset });
  }, [conversationId]);

  // THE DRIVER — the single place side-effects happen, derived from state.
  // `dispatch` / `chatMutation.mutate` / `transport` are stable; re-run on state.
  //
  // The turn's calls are handled strictly one at a time, in order: the active
  // call is the lowest-`order` call still in the loop, and a call becomes active
  // only once every earlier one has settled. We approve it (if needed), run it,
  // wait for its result, then the next call becomes active. Nothing runs ahead.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional, see above
  useEffect(() => {
    const active = selectActiveCall(state);
    if (active) {
      // Only dispatch once it is this call's turn AND it is approved/readonly.
      // AwaitingAuthorization → the prompt is up; Dispatched/Acknowledged → in
      // flight. In every other case we simply wait for the next state change.
      if (active.status === ToolCallStatus.Ready) {
        dispatch({ type: ChatActionType.ToolDispatched, id: active.id, at: Date.now() });
        transport.dispatchToolCall(active.request);
        const timeoutIfStuck = (status: ToolCallStatus) => () => {
          if (stateRef.current.toolCalls[active.id]?.status === status) {
            dispatch({ type: ChatActionType.ToolTimedOut, id: active.id });
          }
        };
        setTimeout(timeoutIfStuck(ToolCallStatus.Dispatched), ACK_TIMEOUT_MS);
        setTimeout(timeoutIfStuck(ToolCallStatus.Acknowledged), RESPONSE_TIMEOUT_MS);
      }
      return;
    }
    // Every call in the turn has settled into the outbox: return all their
    // results to the backend in a single tool turn (not one turn per result).
    if (!state.sending && state.outbox.length > 0) {
      const ids = state.outbox.map((turn) => turn.id);
      const query = state.outbox.map((turn) => turn.query).join('\n\n');
      const toolOutcomes = Object.fromEntries(
        state.outbox.map(({ id, outcome, approvedByUser }) => [String(id), { outcome, approvedByUser }]),
      );
      dispatch({ type: ChatActionType.ToolResultsSent, ids });
      chatMutation.mutate({ query, role: 'tool', toolOutcomes, conversationId, scope: conversationScope });
    }
  }, [state]);

  const approveToolCall = useCallback(() => {
    const req = selectActiveAuthorizationRequest(stateRef.current);
    if (req) dispatch({ type: ChatActionType.ToolAuthorized, id: req.id });
  }, []);
  const rejectToolCall = useCallback(() => {
    const req = selectActiveAuthorizationRequest(stateRef.current);
    if (req) dispatch({ type: ChatActionType.ToolRejected, id: req.id });
  }, []);

  const currentMessages = useCallback(
    () => queryClient.getQueryData<ClientChatResponse[]>(messagesKeyOf()) ?? [],
    [queryClient, messagesKeyOf],
  );

  // Re-send the last user prompt, dropping the trailing (error) message. Reads
  // the live message list from the cache so the callback stays stable.
  const retryLastUserMessage = useCallback(() => {
    const messages = currentMessages();
    const lastUserMessage = [...messages].reverse().find((message) => message.role === 'user');
    if (!lastUserMessage) return;
    chatMutation.mutate({
      query: lastUserMessage.content,
      messages: messages.slice(0, -1),
      conversationId,
      scope: conversationScope,
    });
  }, [currentMessages, chatMutation.mutate, conversationId, conversationScope]);

  // Public send wrappers: stamp the conversation the message is sent from — id *and* scope —
  // onto the variables, so an in-flight response is written where it belongs rather than to
  // whatever is on screen when it resolves (switching dashboards, or leaving dashboard mode
  // mid-analysis, which would otherwise land the reply in the chat root).
  const sendMessage = useCallback<ChatMutation['mutate']>(
    (variables, options) => chatMutation.mutate({ ...variables, conversationId, scope: conversationScope }, options),
    [chatMutation.mutate, conversationId, conversationScope],
  );
  const sendMessageAsync = useCallback<ChatMutation['mutateAsync']>(
    (variables, options) =>
      chatMutation.mutateAsync({ ...variables, conversationId, scope: conversationScope }, options),
    [chatMutation.mutateAsync, conversationId, conversationScope],
  );

  // Conversation feedback. Defined here so the message history is attached
  // automatically — callers only pass the feedback content.
  const feedbackMutation = useMutation<EventResponse, Error, ConversationFeedback, unknown>({
    mutationFn: ({ isPositiveFeedback, categories, feedbackMessage }) =>
      events.createEventApiV1EventsPost({
        eventRequest: {
          type: 'UserFeedback',
          content: {
            positive: isPositiveFeedback,
            feedbackMessage,
            categories,
            conversationId,
            // The generated schema types this as Message[], but feedback ships
            // the full client conversation (a structural superset). Cast keeps
            // the original payload; the mismatch predates this move.
            conversationContent: currentMessages() as unknown as Message[],
          },
          createdAt: new Date(),
        },
      }),
    onSuccess: () => {
      toast.showToast({
        status: 'success',
        autoDismiss: true,
        open: true,
        message: 'Feedback submitted successfully!',
      });
    },
    onError: (error: Error) => {
      logError(error, 'feedbackMutation failed');
    },
  });

  // --- display derivations (carried over from the old useChat) ---

  // Memoized so its identity is stable across renders — otherwise the `value`
  // useMemo below (which depends on visibleChatMessages) would change every
  // render and re-render all useChat() consumers.
  const visibleChatData = useMemo(() => buildChatFeed(chatQueryData, state), [chatQueryData, state]);
  const lastVisible = visibleChatData[visibleChatData.length - 1];

  const hasAssistantMessages = useMemo(() => chatQueryData.some((msg) => msg.role === 'assistant'), [chatQueryData]);

  // Bring each new message into view — the user's on send, the assistant's on arrival.
  useScrollToLastMessage({
    ref: lastChatCardRef,
    // A trail card grows in place, so each row it gains or changes counts as a new message.
    messageKey: lastVisible
      ? `${lastVisible.timestamp.toISOString()}${isToolTrail(lastVisible) ? lastVisible.rows.map((row) => row.status).join() : ''}`
      : null,
    conversationId,
  });

  const historisedMessageCounts = useRef<Record<string, number>>({});
  if (!(conversationId in historisedMessageCounts.current)) {
    historisedMessageCounts.current[conversationId] = visibleChatData.length;
  }

  const prevConversationIdRef = useRef(conversationId);
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll-on-conversation-switch; intentionally not keyed on message length
  useLayoutEffect(() => {
    if (prevConversationIdRef.current !== conversationId) {
      historisedMessageCounts.current[conversationId] = visibleChatData.length;
      prevConversationIdRef.current = conversationId;
      if (chatQueryData.length > 0 && lastChatCardRef.current) {
        requestAnimationFrame(() => {
          lastChatCardRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' });
        });
      }
    }
  }, [conversationId, chatQueryData.length, lastChatCardRef]);

  const isPending = useMutationState({
    filters: { mutationKey: messagesKeyOf() },
    select: (mutation) => mutation.state.status,
  }).includes('pending');

  const retryCount =
    useMutationState({
      filters: { mutationKey: messagesKeyOf(), status: 'pending' },
      select: (mutation) => mutation.state.failureCount,
    })[0] ?? 0;
  const isRetrying = isPending && retryCount > 0;

  const isToolRunning = selectInFlightCount(state) > 0;
  const pendingAuthorizationRequest = selectActiveAuthorizationRequest(state);
  // Busy = a chat request is in flight OR the tool-call loop is mid-cycle
  // (pending/queued tool calls). Drives both the thinking indicator and input
  // disabling, so a user message can't race the loop's next mutation in the gap
  // between an mcp_call settling and its tool-result turn firing.
  const isBusy = isPending || selectIsBusy(state);

  // While the trail shows a tool running or awaiting a decision, it is the progress
  // indicator; the thinking loader only covers the wait on the backend.
  const isThinking = isBusy && !isTrailLive(lastVisible);
  const thinkingMessage = isRetrying ? RETRYING_MESSAGE : THINKING_MESSAGE;

  const visibleChatMessages = useMemo(
    () => (isThinking ? [...visibleChatData, thinkingMessage] : visibleChatData),
    [isThinking, visibleChatData, thinkingMessage],
  );

  const value = useMemo<ChatApi>(
    () => ({
      conversation: {
        messages: visibleChatMessages,
        isNew: chatQueryData.length === 0,
        hasAssistantReplied: hasAssistantMessages,
      },
      conversationMetadata,
      sendMessage,
      sendMessageAsync,
      retryLastMessage: retryLastUserMessage,
      isResponding: isBusy,
      toolAuthorization: {
        pending: pendingAuthorizationRequest,
        isRunning: isToolRunning,
        approve: approveToolCall,
        reject: rejectToolCall,
      },
      feedback: {
        submit: feedbackMutation.mutate,
        isSubmitting: feedbackMutation.isPending,
      },
      rendering: {
        isThinking,
        historisedMessageCount: historisedMessageCounts.current[conversationId] ?? 0,
      },
    }),
    [
      visibleChatMessages,
      conversationMetadata,
      chatQueryData.length,
      hasAssistantMessages,
      sendMessage,
      sendMessageAsync,
      retryLastUserMessage,
      isBusy,
      isThinking,
      pendingAuthorizationRequest,
      approveToolCall,
      rejectToolCall,
      isToolRunning,
      feedbackMutation.mutate,
      feedbackMutation.isPending,
      conversationId,
    ],
  );

  return (
    <ChatContext.Provider value={value}>
      <BackgroundTasksContext.Provider value={backgroundTasks}>{children}</BackgroundTasksContext.Provider>
    </ChatContext.Provider>
  );
};
